<template>
  <div>
    <v-sheet class="w-100">
      <v-row>
        <v-col cols="9">
          <v-text-field v-model="filter"></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-select
            label="Year"
            :items="versions"
            v-model="version"
            @update:model-value="LoadData"
          ></v-select>
        </v-col>
      </v-row>
      <v-data-table
        class="w-100"
        :headers="headers"
        :items="filteredData"
        items-per-page="10"
        item-value="item"
        dense
      >
        <template v-slot:item.modLevel="value">
          {{ getLevel(value.item) }}
        </template>
        <template v-slot:item.balls="value">
          {{ getOtherFields(value.item) }}
        </template>

      </v-data-table>
    </v-sheet>
  </div>  
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref } from "vue";
import useApi from "@/api";
import { IcdoData, Version } from "@a/data";

const api = useApi();

const filter: Ref<string> = ref("");
const version: Ref<string> = ref("ICDO 3.2 2023");
const versions: Ref<Version[]> = ref([]);
const data: Ref<IcdoData[]> = ref([]);

const headers = [
  {title: 'ICD-O 3.2', key: 'icdoCode', width: '10%'},
  {title: 'Level', key: 'modLevel', width: '10%'},
  {title: 'Term', key: 'term', width: '30%'},
  {title: 'Other fields', key: 'balls', width: '50%'},
];

const filteredData = computed((): IcdoData[] => {

  return data.value.filter(
    (d) =>
      d.icdoCode?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.level?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.term?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.reference?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.obs?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.seeAlso?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.seeNote?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.includes?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.excludes?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.otherText?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.seer?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.npcr?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.coc?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.cccr?.toLowerCase().includes(filter.value.toLowerCase()) ||
      d.remarks?.toLowerCase().includes(filter.value.toLowerCase())
  )

});

function getLevel(item: IcdoData): string {
  if(item.level) { return item.level };

  if(item.remarks.includes("New related term")) { return "Synonym"; }
  if(item.remarks.includes("New preferred term")) { return "Preferred"; }

  return "";
}

function getOtherFields(item: IcdoData): string {
  const reference = item.reference ? `Reference: ${item.reference}; ` : "";
  const obs = item.obs ? `Obs: ${item.obs}; ` : "";
  const seeAlso = item.seeAlso ? `See Also: ${item.seeAlso}; ` : "";
  const seeNote = item.seeNote ? `See Note: ${item.seeNote}; ` : "";
  const includes = item.includes ? `Includes: ${item.includes}; ` : "";
  const excludes = item.excludes ? `Excludes: ${item.excludes}; ` : "";
  const otherText = item.otherText ? `Other Text: ${item.otherText}; ` : "";
  const seer = item.seer ? `SEER: ${item.seer}; ` : "";
  const npcr = item.npcr ? `NCPR: ${item.npcr}; ` : "";
  const coc = item.coc ? `COC: ${item.coc}; ` : "";
  const cccr = item.cccr ? `CCCR: ${item.cccr}; ` : "";
  const remarks = item.remarks ? `Remarks: ${item.remarks}; ` : "";
  return `${reference}${obs}${seeAlso}${seeNote}${includes}${excludes}${otherText}${seer}${npcr}${coc}${cccr}${remarks}`;
}

async function Load(): Promise<void> {
  await LoadVersions();
  await LoadData();
}

async function LoadVersions() : Promise<void> {
  const versionsResponse = await api.dataApi.getVersions();
  versions.value = versionsResponse;
}

async function LoadData(): Promise<void> {
  const dataResponse = await api.dataApi.getData2(version.value);
  data.value = dataResponse;
}


onMounted(Load)

</script>